import ProInfluencerIcon from "@/assets/pro_influencer/icon.svg?react";
import {useIsAdmin} from "@/hooks/useUserHooks";
import useInfluencerInviteStore from "@/store/influencerInvite";
import type {TInviteCategory} from "@/store/types";
import omitBy from "lodash/omitBy";
import {useMemo, type ComponentType, type SVGProps} from "react";
import {useTranslation} from "react-i18next";

type TCategory = {
  value: number | null;
  type: TInviteCategory;
  label: string;
  urlPart: string;
  icon?: ComponentType<SVGProps<SVGSVGElement>>;
};

export const categoryUrlParts = {
  LIKED_YOU: "liked_campaigns",
  WORKED_WITH: "previously_worked_with",
  FAVORITE: "favourite_influencers",
  FOLLOWS_YOU: "influencers_following_you",
  RECOMMENDED: "recommended_influencers",
  GROUPS: "groups",
  ADMIN: "all_invitable_influencers",
  PRO_INFLUENCER: "invitable_pro_influencers",
} as const;

const useInviteCategories = () => {
  const {t} = useTranslation("influencer_invite");
  const isAdmin = useIsAdmin();
  const categoryCounts = useInfluencerInviteStore(state => state.categoryCounts);

  const categories = useMemo<{[key in TInviteCategory]: TCategory}>(
    () => ({
      LIKED_YOU: {
        value: categoryCounts["LIKED_YOU"],
        type: "LIKED_YOU",
        label: t("liked_your_campaign"),
        urlPart: categoryUrlParts["LIKED_YOU"],
      },
      WORKED_WITH: {
        value: categoryCounts["WORKED_WITH"],
        type: "WORKED_WITH",
        label: t("influencers_worked_with"),
        urlPart: categoryUrlParts["WORKED_WITH"],
      },
      FAVORITE: {
        value: categoryCounts["FAVORITE"],
        type: "FAVORITE",
        label: t("favorite_influencers"),
        urlPart: categoryUrlParts["FAVORITE"],
      },
      FOLLOWS_YOU: {
        value: categoryCounts["FOLLOWS_YOU"],
        type: "FOLLOWS_YOU",
        label: t("following"),
        urlPart: categoryUrlParts["FOLLOWS_YOU"],
      },
      RECOMMENDED: {
        value: categoryCounts["RECOMMENDED"],
        type: "RECOMMENDED",
        label: t("recommended"),
        urlPart: categoryUrlParts["RECOMMENDED"],
      },
      GROUPS: {
        value: categoryCounts["GROUPS"],
        type: "GROUPS",
        label: t("groups"),
        urlPart: categoryUrlParts["GROUPS"],
      },
      ADMIN: {
        value: categoryCounts["ADMIN"],
        label: t("admin"),
        type: "ADMIN",
        urlPart: categoryUrlParts["ADMIN"],
      },
      PRO_INFLUENCER: {
        value: categoryCounts["PRO_INFLUENCER"],
        label: t("pro_influencers"),
        type: "PRO_INFLUENCER",
        urlPart: categoryUrlParts["PRO_INFLUENCER"],
        icon: ProInfluencerIcon,
      },
    }),
    [categoryCounts, t],
  );

  return omitBy(categories, option => !option.value || (!isAdmin && option.type === "ADMIN")) as typeof categories;
};

export default useInviteCategories;
